import { CanvasObject }   from './canvas.object';
import { StickySide }     from './sticky.side';
import { Logger }         from '@/util/logger';
import { VisualFactory }  from './visuals/visual.factory';
import { Text }           from './visuals/text'
import { FontWeight }     from '@/helpers/fonts/font.weight';
import { FontVariant }    from '@/helpers/fonts/font.variant';
import { FontStyle }      from '@/helpers/fonts/font.style';
import { TextOptions }    from '@/helpers/fonts/text.options';

/**
 *
 * @class BlockTitle
 *
 * title label for link blocks
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2024
 *
 */
export class BlockTitle extends CanvasObject {

  /* styling */

  /** the padding inside the outline */
  public padding: number                    = 8;

  /** color */
  public color: string                      = 'white';

  /** transparency */
  public opacity: number                    = 1.0;

  /** visibility */
  public visible: boolean                   = true;

  /** the font size (height / line height ) */
  public size: number                       = 14;

  /** the actual text to draw */
  public text: string = 'XO';

  /** is which side of this canvas does this block stick to? */
  public sticky: StickySide  = StickySide.TOP;

  /** background rectangle for this block */
  public label: Text | null   = null;

  /**
   *
   * @param parent CanvasObject normally should be a link block
   * @param config Partial<BlockTitle> override the style options
   *
   */
  constructor(
    parent: CanvasObject,
    config: Partial<BlockTitle> = {}) {

    super(parent.canvas(), true);

    /* link up with parent */

    parent.addChildren(this);

    /* use any overrides */

    Object.assign(this, config);

    Logger.debug(`BlockTitle ctor() config: `, config);

    this.label = VisualFactory.buildText(this.titleConfig());

    if (this.label) {

      /* save the handle, so we can use it to manipulate the visual later */

      this.label.handle = this.canvas()?.addShape(this.label);

      this.boundUpdate({
        x:      this.label.x      ?? 0,
        y:      this.label.y      ?? 0,
        width:  this.label.width  ?? 10,
        height: this.label.height ?? 10
      });

    }

  }


  /** generate the configuration for the title text label */
  private titleConfig(): Partial<Text> {

    let x: number              = this.parent()?.x ?? 0;
    let y: number              = this.parent()?.y ?? 0;
    let width: number;
    let height: number         = this.size;
    let text: string           = this.text;
    let color: string          = this.color;
    let family: string         = 'Arial';
    let size: number           = this.size;
    let weight: FontWeight     = FontWeight.BOLD;
    let variation: FontVariant = FontVariant.NORMAL;
    let style: FontStyle       = FontStyle.NORMAL;
    let visible: boolean       = true;
    let opacity: number        = 1.0;
    let rotation: number       = 0.0;

    /* figure out how wide the text is ... */

    width = (new TextOptions({
      text,
      weight,
      style,
      variant: variation,
      size,
      family
    })).measureWidth();

    let left: number = x;
    let top: number  = y;
    let w: number    = this.parent()?.width  ?? 0;
    let h: number    = this.parent()?.height ?? 0;

    if (this.sticky == StickySide.TOP) {

      /* center the text horizontally */

      x = left + (w / 2) - (width / 2);

      /* nudge the text from the top */

      y = top + this.padding;

    } else if (this.sticky == StickySide.BOTTOM) {

      /* center the text horizontally */

      x = left + (w / 2) - (width / 2);

      /* nudge the text from the bottom */

      y = top + h - height - this.padding;

    } else if (this.sticky == StickySide.LEFT) {

      /* center the text horizontally */

      x        = left + this.padding;

      /* nudge the text from the bottom */

      rotation = -90.0;

      y        = top + (h/2) + (width / 2);

    } else if (this.sticky == StickySide.RIGHT) {

      /* center the text horizontally */

      x        = left + w - this.padding;

      /* nudge the text from the bottom */

      rotation = 90.0;

      y        = top + (h/2) - (width / 2);
    }

    /* pass back the specification for the label */

    return {
      x,
      y,
      width,
      height,
      text,
      color,
      family,
      size,
      weight,
      variation,
      style,
      visible,
      opacity,
      rotation
    };
  }

  /** upon return object should have updated its layout or geometry as needed. */
  public layout(): CanvasObject {

    /*
     * the screen geometry has changed somehow, so we have to recalculate our layout and
     * then get the canvas visuals to update in place.
     *
     */

    let handle: any = this.label?.handle ?? null;

    if (!handle) {

      /* nothing to do layout on yet */

      return this;
    }

    this.label        = VisualFactory.buildText(this.titleConfig());
    this.label.handle = handle;

    this.boundUpdate({
      x:      this.label.x      ?? 0,
      y:      this.label.y      ?? 0,
      width:  this.label.width  ?? 10,
      height: this.label.height ?? 10
    });

    this.canvas()?.updateShape(this.label);

    return this;
  }

}
